import {
  ControlModeTemplateField,
  ControlModeTemplateFieldsType,
  ControlModeTemplateSettings,
} from '@src/types/ControlModes';
import { FormValue } from '../context/formReducer';
import {
  IStringInputFieldControlMode,
  INumberInputFieldControlMode,
  IBooleanInputFieldControlMode,
  ISelectInputFieldControlMode,
  Stack,
} from '@src/types/Stacks';

/**
 * Converts a template field to a stack field based on its type
 */
export const convertFieldsToStackFormat = (field: ControlModeTemplateField) => {
  // Convert template field to stack field based on type
  switch (field.type) {
    case ControlModeTemplateFieldsType.STRING: {
      // Create a properly typed string field
      const stringField: IStringInputFieldControlMode = {
        uiLabel: field.uiLabel,
        controllerField: field.controllerField,
        type: ControlModeTemplateFieldsType.STRING,
        quickConfig: field.quickConfig || false,
        value: field.defaultValue || '',
      };
      return stringField;
    }
    case ControlModeTemplateFieldsType.NUMBER: {
      // Create a properly typed number field
      const numberField: INumberInputFieldControlMode = {
        uiLabel: field.uiLabel,
        controllerField: field.controllerField,
        type: ControlModeTemplateFieldsType.NUMBER,
        quickConfig: field.quickConfig || false,
        value:
          field.defaultValue !== undefined ? Number(field.defaultValue) : 0,
      };
      // Only add range if it exists
      if (field.range) {
        numberField.range = field.range;
      }
      return numberField;
    }
    case ControlModeTemplateFieldsType.BOOLEAN: {
      // Create a properly typed boolean field
      const booleanField: IBooleanInputFieldControlMode = {
        uiLabel: field.uiLabel,
        controllerField: field.controllerField,
        type: ControlModeTemplateFieldsType.BOOLEAN,
        quickConfig: field.quickConfig || false,
        value:
          field.defaultValue !== undefined
            ? Boolean(field.defaultValue)
            : false,
      };
      return booleanField;
    }
    case ControlModeTemplateFieldsType.SELECT: {
      // Create a properly typed select field
      const selectField: ISelectInputFieldControlMode = {
        uiLabel: field.uiLabel,
        controllerField: field.controllerField,
        type: ControlModeTemplateFieldsType.SELECT,
        quickConfig: field.quickConfig || false,
        listOptions: field.listOptions || [],
        value:
          field.defaultValue ||
          (field.listOptions && field.listOptions.length > 0
            ? field.listOptions[0]
            : ''),
      };
      return selectField;
    }
    default: {
      // Default to string field if type is unknown
      // Cast field to ControlModeTemplateField to avoid 'never' type issues
      const f = field as ControlModeTemplateField;
      const defaultField: IStringInputFieldControlMode = {
        uiLabel: f.uiLabel,
        controllerField: f.controllerField,
        type: ControlModeTemplateFieldsType.STRING,
        quickConfig: f.quickConfig || false,
        value: '',
      };
      return defaultField;
    }
  }
};

/**
 * Converts stack data to template format
 * @param stack The stack object to convert
 * @returns Object containing formValues and groupedFields
 */
export const convertStackToTemplateFormat = (stack: Stack) => {
  const newFormValues: Record<string, FormValue> = {};
  const newGroupedFields: {
    uiLabel: string;
    controllerField: string;
    fields: ControlModeTemplateField[];
  }[] = [];

  // Extract settings from the first control mode or use an empty array
  const controlMode =
    stack.controlModes && stack.controlModes.length > 0
      ? stack.controlModes[0]
      : { sections: [] };

  // Map sections to settings format
  const extractedSettings = controlMode.sections.map((section) => {
    // Create a setting from each section
    const setting: ControlModeTemplateSettings = {
      controllerField: section.controllerField,
      uiLabel: section.uiLabel,
      fields: section.fields.map((field) => {
        // Convert stack field to template field based on type
        let templateField: ControlModeTemplateField;

        switch (field.type) {
          case ControlModeTemplateFieldsType.STRING: {
            const stringField = field as IStringInputFieldControlMode;
            templateField = {
              uiLabel: stringField.uiLabel,
              controllerField: stringField.controllerField,
              type: ControlModeTemplateFieldsType.STRING,
              quickConfig: stringField.quickConfig,
              defaultValue: stringField.value,
            };
            // Set form value
            newFormValues[stringField.controllerField] = stringField.value;
            break;
          }
          case ControlModeTemplateFieldsType.NUMBER: {
            const numberField = field as INumberInputFieldControlMode;
            templateField = {
              uiLabel: numberField.uiLabel,
              controllerField: numberField.controllerField,
              type: ControlModeTemplateFieldsType.NUMBER,
              quickConfig: numberField.quickConfig,
              defaultValue: numberField.value,
              range: numberField.range,
            };
            // Set form value
            newFormValues[numberField.controllerField] = numberField.value;
            break;
          }
          case ControlModeTemplateFieldsType.BOOLEAN: {
            const booleanField = field as IBooleanInputFieldControlMode;
            templateField = {
              uiLabel: booleanField.uiLabel,
              controllerField: booleanField.controllerField,
              type: ControlModeTemplateFieldsType.BOOLEAN,
              quickConfig: booleanField.quickConfig,
              defaultValue: booleanField.value,
            };
            // Set form value
            newFormValues[booleanField.controllerField] = booleanField.value;
            break;
          }
          case ControlModeTemplateFieldsType.SELECT: {
            const selectField = field as ISelectInputFieldControlMode;
            templateField = {
              uiLabel: selectField.uiLabel,
              controllerField: selectField.controllerField,
              type: ControlModeTemplateFieldsType.SELECT,
              quickConfig: selectField.quickConfig,
              defaultValue: selectField.value,
              listOptions: selectField.listOptions || [],
            };
            // Set form value
            newFormValues[selectField.controllerField] = selectField.value;
            break;
          }
          default: {
            // Default to string field
            const defaultField = field as IStringInputFieldControlMode;
            templateField = {
              uiLabel: defaultField.uiLabel,
              controllerField: defaultField.controllerField,
              type: ControlModeTemplateFieldsType.STRING,
              quickConfig: defaultField.quickConfig,
              defaultValue: defaultField.value,
            };
            // Set form value
            newFormValues[defaultField.controllerField] = defaultField.value;
          }
        }

        return templateField;
      }),
    };

    // Add to grouped fields
    newGroupedFields.push({
      uiLabel: section.uiLabel,
      controllerField: section.controllerField,
      fields: setting.fields,
    });

    return setting;
  });

  return {
    formValues: newFormValues,
    groupedFields: newGroupedFields,
    settings: extractedSettings,
  };
};
